import { useEffect, useRef, useState } from 'react'
import { alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import CardSectionHeader from 'src/v3/components/card-section-header'
import type { EventAttendanceMechanism } from '../types'
import { postCompletionEvent } from '../utils/api'

dayjs.extend(utc)
dayjs.extend(timezone)

let LOCAL_TZ = dayjs.tz.guess()

type AttendanceCardProps = {
  sessionID: string
  sessionName: string
  sessionTime: string
  mechanism: EventAttendanceMechanism
  selfCheckInOpen: boolean
  onSuccess: (msg: string) => void
  onError: (msg: string) => void
  onRecorded: () => void
  onCheckAgain: () => void
  // Controlled so the Requires attention session row can open the same passcode dialog.
  dialogOpen: boolean
  onDialogOpenChange: (open: boolean) => void
}

let AttendanceCard = ({
  sessionID,
  sessionName,
  sessionTime,
  mechanism,
  selfCheckInOpen,
  onSuccess,
  onError,
  onRecorded,
  onCheckAgain,
  dialogOpen,
  onDialogOpenChange: setDialogOpen,
}: AttendanceCardProps) => {
  let [passcode, setPasscode] = useState('')
  let [passcodeError, setPasscodeError] = useState(false)
  let [loading, setLoading] = useState(false)
  let [checking, setChecking] = useState(false)
  let checkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (checkTimerRef.current != null) clearTimeout(checkTimerRef.current)
    },
    [],
  )

  let handleCheckAgain = () => {
    if (checkTimerRef.current != null) clearTimeout(checkTimerRef.current)
    setChecking(true)
    onCheckAgain()
    // Reset after 3s if the component hasn't unmounted (attendance still pending).
    // eslint-disable-next-line fp/no-mutation
    checkTimerRef.current = setTimeout(() => setChecking(false), 3000)
  }

  let displayTime = dayjs
    .utc(sessionTime)
    .tz(LOCAL_TZ)
    .format('ddd, MMM D · h:mm A z')

  let handleClose = () => {
    setDialogOpen(false)
    setPasscode('')
    setPasscodeError(false)
  }

  let isPasscode = mechanism.kind === 'quizPasscode'
  // Past the self check-in window only a manager can record attendance.
  let canSelfCheckIn = isPasscode && selfCheckInOpen

  let handleConfirm = () => {
    // The server owns passcode validation; a wrong code comes back as a 403.
    let enteredPasscode = passcode.trim()
    if (isPasscode && enteredPasscode === '') {
      setPasscodeError(true)
      return
    }
    setLoading(true)
    postCompletionEvent(sessionID, isPasscode ? enteredPasscode : undefined)
      .then(() => {
        onRecorded()
        onSuccess('Attendance confirmed')
        handleClose()
      })
      .catch((err) => {
        let message = err?.response?.data
        let status = err?.response?.status
        if (status === 409) {
          // Lost a race with another attendance write; refresh to show its outcome.
          onError(
            typeof message === 'string' && message !== ''
              ? message
              : 'Attendance could not be recorded. Please refresh and try again.',
          )
          handleClose()
          onRecorded()
        } else if (status !== 403) onError('Error recording attendance')
        else if (
          isPasscode &&
          typeof message === 'string' &&
          message.toLowerCase().includes('passcode')
        )
          setPasscodeError(true)
        else {
          // Rejected on state we have cached (window closed, invite changed); refresh.
          onError(
            typeof message === 'string' && message !== ''
              ? message
              : 'You are not authorized to record attendance for this event.',
          )
          handleClose()
          onRecorded()
        }
      })
      .finally(() => setLoading(false))
  }

  return (
    <Paper
      className="flex flex-col gap-3 p-6"
      sx={(theme) => ({
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        borderTop: `3px solid ${theme.palette.primary.main}`,
      })}
    >
      <CardSectionHeader label="Record Attendance" />
      <Box className="flex flex-col gap-1">
        <Typography sx={{ fontWeight: 500 }}>{sessionName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {displayTime}
        </Typography>
      </Box>
      {canSelfCheckIn ? (
        <Button
          variant="contained"
          fullWidth
          onClick={() => setDialogOpen(true)}
        >
          Record Attendance
        </Button>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {isPasscode
              ? 'Self check-in for this session has closed. Ask your event manager to record your attendance.'
              : 'Your attendance will be confirmed by your instructor.'}
          </Typography>
          <Button
            variant="outlined"
            fullWidth
            loading={checking}
            onClick={handleCheckAgain}
          >
            Check again
          </Button>
        </>
      )}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!loading) handleClose()
        }}
        aria-labelledby="attendance-dialog-title"
      >
        <Box className="flex flex-col gap-4 p-6" sx={{ minWidth: 320 }}>
          <Typography
            id="attendance-dialog-title"
            variant="h6"
            className="text-center"
          >
            Confirm Attendance
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            label="Event Passcode"
            value={passcode}
            onChange={(e) => {
              setPasscode(e.target.value)
              setPasscodeError(false)
            }}
            error={passcodeError}
            helperText={passcodeError ? 'Incorrect event passcode' : undefined}
          />
          <Box className="flex justify-end gap-2">
            <Button variant="outlined" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              loading={loading}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Paper>
  )
}

export default AttendanceCard
